import { totpQrDataUri } from "./totp-qr.js";
import { useEffect, useState, type FormEvent } from "react";
import {
  ArrowRight,
  BadgeCheck,
  BookOpen,
  Boxes,
  Building2,
  Check,
  ChevronRight,
  CircleCheckBig,
  Cloud,
  Code2,
  FileCheck2,
  Fingerprint,
  Gauge,
  Globe2,
  KeyRound,
  Laptop,
  Layers3,
  LockKeyhole,
  Menu,
  Network,
  RefreshCw,
  ScrollText,
  Server,
  ShieldCheck,
  ShieldEllipsis,
  Sparkles,
  TerminalSquare,
  Users,
  X,
  Zap,
} from "lucide-react";
import {
  WorkspaceSignup,
  WorkspaceVerification,
} from "./WorkspaceOnboarding.js";
import {
  createPasskeyCredential,
  getPasskeyAssertion,
  passkeysSupported,
  type PasskeyAuthenticationOptions,
  type PasskeyEnrollmentOptions,
} from "./passkeys.js";

const protocols = ["ACME", "SPIFFE", "MCP", "A2A", "mTLS", "Signed discovery"];
const trustSteps = [
  [
    "01",
    "Workload requests",
    "A workload authenticates locally and asks for a tightly scoped identity.",
  ],
  [
    "02",
    "Your node decides",
    "Your policy evaluates the exact CSR, SANs, algorithm, validity, and requester.",
  ],
  [
    "03",
    "Sectigo decides",
    "The edge independently evaluates the same immutable transaction digest.",
  ],
  [
    "04",
    "Your CA signs",
    "Only dual-approved evidence reaches Sectigo SCM or your protected CA signer.",
  ],
];

function Logo() {
  return (
    <a className="mk-logo" href="/" aria-label="Sectigo Edge home">
      <img src="/sectigo-logo.svg" alt="Sectigo" />
      <i />
      <span>EDGE</span>
    </a>
  );
}

function Nav({ docs = false }: { docs?: boolean }) {
  const [open, setOpen] = useState(false);
  return (
    <header className="mk-nav">
      <Logo />
      <nav className={open ? "open" : ""} aria-label="Primary navigation">
        <a href={docs ? "/#platform" : "#platform"}>Platform</a>
        <a href={docs ? "/#security" : "#security"}>Security</a>
        <a href={docs ? "/#rotation" : "#rotation"}>Zero-downtime rotation</a>
        <a href="/docs">Docs</a>
        <span className="mk-nav-auth">
          <a href="/login">Log in</a>
          <a className="mk-nav-signup" href="/signup">
            Create account
          </a>
          <a className="mk-nav-console" href="/console/demo">
            Explore demo <ArrowRight size={14} />
          </a>
        </span>
      </nav>
      <button
        className="mk-menu"
        onClick={() => setOpen(!open)}
        aria-label={open ? "Close menu" : "Open menu"}
      >
        {open ? <X /> : <Menu />}
      </button>
    </header>
  );
}

function TrustDiagram() {
  return (
    <div
      className="mk-trust-diagram"
      role="img"
      aria-label="A workload request needs approval from both the customer Mesh Node and Sectigo Edge Trust Service before the CA signs"
    >
      <div className="mk-diagram-request">
        <span>
          <Code2 />
        </span>
        <b>Workload</b>
        <small>CSR + identity</small>
      </div>
      <div className="mk-diagram-line">
        <i />
        <em>one immutable request</em>
        <i />
      </div>
      <div className="mk-diagram-gates">
        <article>
          <span className="mk-gate-icon">
            <Server />
          </span>
          <small>YOUR TRUST DOMAIN</small>
          <strong>Mesh Node</strong>
          <p>Local policy + hardware-key approval</p>
          <b>
            <Check /> Approved
          </b>
        </article>
        <div className="mk-plus">+</div>
        <article>
          <span className="mk-gate-icon">
            <Cloud />
          </span>
          <small>SECTIGO TRUST DOMAIN</small>
          <strong>Edge Trust Service</strong>
          <p>Platform policy + replay defense</p>
          <b>
            <Check /> Approved
          </b>
        </article>
      </div>
      <div className="mk-diagram-converge">
        <i />
        <span>Both trust domains must approve</span>
        <i />
      </div>
      <div className="mk-diagram-ca">
        <KeyRound />
        <div>
          <small>SECTIGO SCM OR YOUR CA</small>
          <strong>Certificate issued</strong>
        </div>
        <BadgeCheck />
      </div>
    </div>
  );
}

function RotationDiagram() {
  return (
    <div className="mk-rotation-diagram">
      <div className="mk-rotation-head">
        <span>LIVE CERTIFICATE SET</span>
        <b>payments.prod</b>
        <em>Generation 42</em>
      </div>
      <div className="mk-slots">
        <article className="mk-slot-active">
          <header>
            <span>ACTIVE</span>
            <b>
              <i /> SERVING
            </b>
          </header>
          <KeyRound />
          <strong>Serial 8F:27:9A</strong>
          <p>Traffic stays here throughout staging.</p>
          <footer>Expires in 28 days</footer>
        </article>
        <div className="mk-slot-flow">
          <span>
            <RefreshCw />
          </span>
          <b>
            HEALTH
            <br />
            GATE
          </b>
          <i />
        </div>
        <article className="mk-slot-standby">
          <header>
            <span>STANDBY</span>
            <b>
              <i /> VERIFIED
            </b>
          </header>
          <KeyRound />
          <strong>Serial A3:41:CC</strong>
          <p>Key match, chain, config, and app probe pass.</p>
          <footer>Ready to promote</footer>
        </article>
      </div>
      <div className="mk-rotation-result">
        <CircleCheckBig />
        <span>
          <b>Atomic promotion.</b> The prior certificate remains available for
          instant rollback during the overlap window.
        </span>
      </div>
    </div>
  );
}

function AgentTrustDiagram() {
  return (
    <div
      className="mk-agent-diagram"
      role="img"
      aria-label="A service announces itself without gaining trust, an administrator claims it, and Sectigo Edge then issues a short-lived SPIFFE identity for signed MCP and agent channels"
    >
      <header>
        <span>PROACTIVE REGISTRATION</span>
        <b>Unknown service → managed identity</b>
      </header>
      <div className="mk-agent-flow">
        <article>
          <Code2 />
          <small>UNCLAIMED</small>
          <strong>Service announces</strong>
          <p>Ephemeral key · runtime evidence · capabilities</p>
        </article>
        <i>
          <ArrowRight />
        </i>
        <article>
          <Server />
          <small>LOCAL PROOF</small>
          <strong>Node challenges</strong>
          <p>Signed nonce · possession proof · quarantine</p>
        </article>
        <i>
          <ArrowRight />
        </i>
        <article>
          <Users />
          <small>ADMIN CONTROL</small>
          <strong>Owner claims</strong>
          <p>MFA · exact role · policy · audit evidence</p>
        </article>
      </div>
      <div className="mk-agent-result">
        <Fingerprint />
        <div>
          <small>SHORT-LIVED WORKLOAD IDENTITY</small>
          <b>SPIFFE mTLS + signed, audience-bound messages</b>
        </div>
        <span>MCP</span>
        <span>A2A</span>
        <span>gRPC</span>
      </div>
    </div>
  );
}

export function LandingPage() {
  return (
    <div className="mk-page">
      <Nav />
      <main>
        <section className="mk-hero">
          <div className="mk-hero-glow" />
          <div className="mk-hero-grid" />
          <div className="mk-hero-copy">
            <div className="mk-kicker">
              <span /> SECTIGO EDGE
            </div>
            <h1>
              Edge security.
              <br />
              <em>Reliability at scale.</em>
            </h1>
            <p>
              Sectigo Edge pairs a customer-operated trust node with Sectigo’s
              globally distributed trust service. Every certificate needs both
              sides to approve the exact same request—without inbound firewall
              rules or cloud-held private keys.
            </p>
            <div className="mk-hero-actions">
              <a className="mk-primary" href="/signup">
                Create your account <ArrowRight />
              </a>
              <a className="mk-secondary" href="/console/demo">
                <Gauge /> Explore the demo
              </a>
            </div>
            <div className="mk-hero-proof">
              <span>
                <ShieldCheck /> Dual-control issuance
              </span>
              <span>
                <Network /> Local-first automation
              </span>
              <span>
                <RefreshCw /> Zero-downtime rotation
              </span>
            </div>
          </div>
          <div className="mk-hero-visual">
            <TrustDiagram />
          </div>
        </section>

        <section className="mk-logo-strip">
          <span>STANDARDS-FIRST AUTOMATION</span>
          {protocols.map((name) => (
            <b key={name}>{name}</b>
          ))}
        </section>

        <section className="mk-section mk-intro" id="platform">
          <div className="mk-section-label">A DIFFERENT TRUST MODEL</div>
          <div className="mk-intro-head">
            <h2>
              Compromise one side.
              <br />
              Your CA still says no.
            </h2>
            <p>
              Traditional hosted PKI concentrates authorization in one
              administrative plane. Sectigo Edge makes the customer environment
              a required participant in every issuance, while Sectigo retains
              independent policy and lifecycle controls.
            </p>
          </div>
          <div className="mk-step-grid">
            {trustSteps.map(([number, title, text]) => (
              <article key={number}>
                <span>{number}</span>
                <ShieldCheck />
                <h3>{title}</h3>
                <p>{text}</p>
              </article>
            ))}
          </div>
        </section>

        <section className="mk-dark-section" id="security">
          <div className="mk-dark-inner">
            <div className="mk-dark-copy">
              <div className="mk-section-label">BUILT TO FAIL CLOSED</div>
              <h2>
                Global speed.
                <br />
                <em>Enterprise control.</em>
              </h2>
              <p>
                Sectigo Edge handles global ingress, isolation, replay
                protection, durable tenant state, and observability. Your
                hardened Windows or Linux node authenticates workloads and makes
                the customer-side decision locally.
              </p>
              <ul>
                <li>
                  <Check /> Outbound-only node connectivity
                </li>
                <li>
                  <Check /> mTLS plus signed transaction envelopes
                </li>
                <li>
                  <Check /> Per-tenant strongly consistent security state
                </li>
                <li>
                  <Check /> Signed, hash-chained evidence on both sides
                </li>
              </ul>
              <a href="/docs#security">
                Read the security model <ArrowRight />
              </a>
            </div>
            <div className="mk-cloud-stack">
              <article>
                <small>GLOBAL FRONT DOOR</small>
                <Globe2 />
                <strong>Sectigo Edge Network</strong>
                <p>Mutual TLS · threat protection · identity · rate controls</p>
              </article>
              <i />
              <article>
                <small>SECTIGO CONTROL PLANE</small>
                <Layers3 />
                <strong>Edge Trust Service</strong>
                <p>Tenant isolation · durable policy · evidence · automation</p>
              </article>
              <i />
              <article>
                <small>YOUR SIGNING AUTHORITY</small>
                <KeyRound />
                <strong>Sectigo SCM or BYO CA</strong>
                <p>Remote HSM signer · immutable approval evidence</p>
              </article>
            </div>
          </div>
        </section>

        <section className="mk-section mk-features">
          <div className="mk-section-label">OPERATE TRUST, NOT TICKETS</div>
          <div className="mk-feature-head">
            <h2>
              Everything serious PKI teams need.
              <br />
              <em>Nothing hidden behind proprietary clients.</em>
            </h2>
            <a href="/docs">
              View documentation <ArrowRight />
            </a>
          </div>
          <div className="mk-feature-grid">
            <article className="mk-feature-large">
              <span>
                <Gauge />
              </span>
              <div>
                <small>TRUST OPERATIONS</small>
                <h3>One live view of both trust domains</h3>
                <p>
                  See node health, policy decisions, certificate exposure,
                  cryptographic readiness, signing providers, and the
                  independent evidence chain in one Sectigo workspace.
                </p>
                <a href="/console/demo">
                  Open product preview <ChevronRight />
                </a>
              </div>
              <div className="mk-mini-console">
                <header>
                  <i />
                  <i />
                  <i />
                  <b>Trust health</b>
                </header>
                <div>
                  <span>
                    <small>TRUST SCORE</small>
                    <strong>99.98%</strong>
                  </span>
                  <span>
                    <small>HEALTHY NODES</small>
                    <strong>3 / 3</strong>
                  </span>
                </div>
                <footer>
                  <i />
                  <b>Both trust domains healthy</b>
                </footer>
              </div>
            </article>
            <article>
              <span>
                <Fingerprint />
              </span>
              <small>HARDWARE-ROOTED EDGE</small>
              <h3>TPM, CNG, and HSM identities</h3>
              <p>
                Mesh Node keys can be non-exportable in Windows TPM/CNG, Linux
                TPM2, or PKCS#11 HSMs. Signed discovery carries protection and
                attestation pins.
              </p>
            </article>
            <article>
              <span>
                <Users />
              </span>
              <small>ENTERPRISE ACCESS</small>
              <h3>SSO, MFA, SCIM, simple roles</h3>
              <p>
                Sectigo Edge verifies enterprise SAML/OIDC sessions and MFA.
                Resource-aware authorization sits behind five understandable
                roles.
              </p>
            </article>
            <article>
              <span>
                <Sparkles />
              </span>
              <small>CRYPTO INTELLIGENCE</small>
              <h3>Make PQ migration evidence-based</h3>
              <p>
                Discover what endpoints can actually consume, model policy as
                data, and stage reversible migration waves before deadlines
                become incidents.
              </p>
            </article>
            <article>
              <span>
                <ScrollText />
              </span>
              <small>AUDIT EVIDENCE</small>
              <h3>Reconstruct every issuance</h3>
              <p>
                Canonical request digests, both decisions, signer receipts,
                deployment state, and chained audit heads form a portable
                evidence bundle.
              </p>
            </article>
            <article>
              <span>
                <Network />
              </span>
              <small>AGENT AND MCP TRUST</small>
              <h3>Identity for every autonomous action</h3>
              <p>
                SPIFFE mTLS, signed MCP/A2A messages, exact audiences, replay
                defense, and bounded delegation secure agents without treating
                prompt text as authorization.
              </p>
            </article>
          </div>
        </section>

        <section className="mk-agent-section" id="agents">
          <div className="mk-agent-copy">
            <div className="mk-section-label">DISCOVER. CLAIM. TRUST.</div>
            <h2>
              Services can introduce themselves.
              <br />
              <em>They cannot approve themselves.</em>
            </h2>
            <p>
              The Agent Trust SDK lets a new workload announce a measured,
              short-lived capability lease to the nearest Mesh Node. The node
              verifies proof of key possession and quarantines it until an
              authorized administrator approves the exact requested SPIFFE
              identity, owner, CA profile, and channel permissions.
            </p>
            <ul>
              <li>
                <Check /> No reusable bootstrap secret in registration
              </li>
              <li>
                <Check /> Local discovery with connectivity-only cloud failback
              </li>
              <li>
                <Check /> Signed MCP, A2A, HTTP, gRPC, and event envelopes
              </li>
              <li>
                <Check /> Delegation can only narrow scope, resource, lifetime,
                and depth
              </li>
            </ul>
            <a href="/docs#agents">
              Explore agent trust architecture <ArrowRight />
            </a>
          </div>
          <AgentTrustDiagram />
        </section>

        <section className="mk-rotation-section" id="rotation">
          <div className="mk-rotation-copy">
            <div className="mk-section-label">
              OPTIONAL DUAL-CERTIFICATE MODE
            </div>
            <h2>
              Rotate certificates.
              <br />
              <em>Never rotate into an outage.</em>
            </h2>
            <p>
              Sectigo Edge stages the new certificate beside the one already
              serving. Deterministic adapters validate the key, chain,
              configuration, and application health before an atomic promotion.
              Failed checks leave the active slot untouched.
            </p>
            <div className="mk-rotation-points">
              <span>
                <Check /> Configurable overlap window
              </span>
              <span>
                <Check /> Health-gated auto or manual promotion
              </span>
              <span>
                <Check /> Idempotent restart recovery
              </span>
              <span>
                <Check /> One-click rollback window
              </span>
            </div>
          </div>
          <RotationDiagram />
        </section>

        <section className="mk-section mk-ca">
          <div className="mk-section-label">YOUR CA, YOUR CHOICE</div>
          <h2>
            Connect Sectigo SCM.
            <br />
            Or bring the CA you already trust.
          </h2>
          <div className="mk-ca-grid">
            <article>
              <span>
                <ShieldCheck />
              </span>
              <small>SECTIGO CERTIFICATE MANAGER</small>
              <h3>Managed private CA</h3>
              <p>
                Authorize an SCM account, select a private CA and profile, and
                let Sectigo Edge send immutable dual-approval evidence to the
                protected signing boundary.
              </p>
              <a href="/docs#ca-connectors">
                SCM connector guide <ArrowRight />
              </a>
            </article>
            <article>
              <span>
                <KeyRound />
              </span>
              <small>BRING YOUR OWN CA</small>
              <h3>One-time secure import</h3>
              <p>
                Complete a time-limited key ceremony directly with the remote
                signer. Sectigo Edge retains only the public chain, opaque key
                reference, and ceremony receipt.
              </p>
              <a href="/docs#ca-connectors">
                BYO CA ceremony <ArrowRight />
              </a>
            </article>
          </div>
        </section>

        <section className="mk-proof-section">
          <div>
            <small>THE EDGE FITS YOUR ESTATE</small>
            <h2>
              Windows. Linux. Kubernetes.
              <br />
              Open protocols everywhere.
            </h2>
          </div>
          <div className="mk-platform-grid">
            <span>
              <Server /> Linux service
            </span>
            <span>
              <Boxes /> Kubernetes Helm
            </span>
            <span>
              <TerminalSquare /> Windows service
            </span>
            <span>
              <Cloud /> Container / VM
            </span>
            <span>
              <Globe2 /> Sectigo Edge
            </span>
            <span>
              <Code2 /> TypeScript SDK
            </span>
          </div>
        </section>

        <section className="mk-cta">
          <div className="mk-cta-grid" />
          <span>
            <LockKeyhole />
          </span>
          <small>PRIVATE PKI WITHOUT UNILATERAL CONTROL</small>
          <h2>
            Make every certificate
            <br />a two-party decision.
          </h2>
          <p>
            Create a Sectigo Edge workspace or explore the full trust operations
            experience with demo data.
          </p>
          <div>
            <a className="mk-primary" href="/signup">
              Create account <ArrowRight />
            </a>
            <a className="mk-secondary mk-secondary-dark" href="/console/demo">
              Explore demo
            </a>
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
}

interface EnterpriseAuthConfig {
  enterprise_sso_enabled: boolean;
  enterprise_sso_configured: boolean;
  enterprise_sso_ready: boolean;
  password_auth_enabled: boolean;
  password_access_ready: boolean;
  password_recovery_enabled: boolean;
  passkey_mfa_enabled: boolean;
  phishing_resistant_mfa_required: boolean;
  production_access_ready: boolean;
  mfa_required: boolean;
  mfa_enforcement_mode: "access_policy" | "jwt_amr" | "not_configured";
  immutable_subject_configured: boolean;
  authorization_enabled: boolean;
  scim_supported: boolean;
  scim_enforced: boolean;
  login_url?: string;
  missing_controls: string[];
}

function EnterpriseSsoButton() {
  const [config, setConfig] = useState<EnterpriseAuthConfig>();
  const [error, setError] = useState("");
  useEffect(() => {
    let active = true;
    void fetch("/api/v1/auth/config", { headers: { accept: "application/json" } })
      .then(async (response) => {
        if (!response.headers.get("content-type")?.includes("application/json")) {
          throw new Error("The identity service returned an unexpected response.");
        }
        const body = await response.json() as EnterpriseAuthConfig & { error?: { message?: string } };
        if (!response.ok) throw new Error(body.error?.message ?? "Enterprise identity is unavailable.");
        if (active) setConfig(body);
      })
      .catch((reason: unknown) => {
        if (active) setError(reason instanceof Error ? reason.message : "Enterprise identity is unavailable.");
      });
    return () => { active = false; };
  }, []);

  if (config?.enterprise_sso_ready && config.login_url) {
    return <a className="mk-auth-primary" href={config.login_url}>Continue to SSO <ArrowRight /></a>;
  }
  if (config && !config.enterprise_sso_ready) {
    const labels: Record<string, string> = {
      enterprise_sso: "enterprise SSO",
      identity_gateway_verification: "signed-session verification",
      immutable_subject: "immutable identity binding",
      mfa: "MFA enforcement",
      fine_grained_authorization: "fine-grained authorization",
      scim_enforcement: "SCIM lifecycle enforcement",
    };
    const missing = config.missing_controls.map((control) => labels[control] ?? control).join(", ");
    return <div className="mk-sso-unavailable" role="status">
      <ShieldEllipsis />
      <div>
        <strong>{config.enterprise_sso_configured ? "Production access controls are incomplete" : "This domain is ready; your organization is not connected yet"}</strong>
        <span>{config.enterprise_sso_configured ? `SSO remains closed until these required controls are healthy: ${missing}.` : "Connect your enterprise identity provider to enable SSO. A Sectigo Edge account remains available above."}</span>
      </div>
      <div className="mk-sso-actions">
        <a className="mk-sso-action mk-sso-action--primary" href="/signup">Create a workspace <ArrowRight /></a>
        <a className="mk-sso-action" href="/console/demo"><Gauge /> Open demo console</a>
      </div>
    </div>;
  }
  return <>
    <button
      className="mk-auth-primary mk-auth-primary--button"
      type="button"
      disabled={!config && !error}
      onClick={() => setError("The enterprise identity service could not be reached. Retry after checking the deployment status, or open the isolated demo.")}
    >
      {!config && !error ? <><RefreshCw className="spin" /> Checking SSO…</> : <>Continue to SSO <ArrowRight /></>}
    </button>
    {error && <p className="mk-auth-inline-error" role="alert">{error}</p>}
  </>;
}

interface PasswordAuthResult {
  authenticated?: boolean;
  account_status?: "pending_workspace" | "rejected";
  message?: string;
  challenge?: string;
  manual_key?: string;
  otpauth_uri?: string;
  redirect_url?: string;
  tenant_id?: string;
  mfa_method?: "totp" | "passkey";
  phishing_resistant_mfa?: boolean;
  passkey_enrollment_recommended?: boolean;
  passkey_authentication?: PasskeyAuthenticationOptions;
  error?: { message?: string };
}

function PasswordLogin() {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [challenge, setChallenge] = useState("");
  const [manualKey, setManualKey] = useState("");
  const [otpauthUri, setOtpauthUri] = useState("");
  const [code, setCode] = useState("");
  const [passkeyAuthentication, setPasskeyAuthentication] = useState<PasskeyAuthenticationOptions>();
  const [postAuthentication, setPostAuthentication] = useState<PasswordAuthResult>();
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [notice, setNotice] = useState(() => new URLSearchParams(location.search).get("password_reset") === "1"
    ? "Your password was changed and every previous session was signed out. Continue with your new password and existing MFA."
    : "");

  async function call<T extends { error?: { message?: string } } = PasswordAuthResult>(path: string, body: unknown): Promise<T> {
    const response = await fetch(path, { method: "POST", headers: { "content-type": "application/json" }, body: JSON.stringify(body) });
    const result = await response.json().catch(() => ({})) as T;
    if (!response.ok) throw new Error(result.error?.message ?? "Sign-in could not be completed");
    return result;
  }

  function authenticationCompleted(result: PasswordAuthResult) {
    if (!result.authenticated || !result.redirect_url) throw new Error("The authenticated session could not be created.");
    if (result.passkey_enrollment_recommended && passkeysSupported()) setPostAuthentication(result);
    else location.assign(result.redirect_url);
  }

  async function submitPassword(event: FormEvent) {
    event.preventDefault(); setBusy(true); setError(""); setNotice("");
    try {
      const result = await call("/api/v1/auth/password/login", { email, password });
      if (result.account_status) { setNotice(result.message ?? "Your workspace is not active yet."); return; }
      if (!result.challenge) throw new Error("The authentication service returned an incomplete challenge.");
      setChallenge(result.challenge); setManualKey(result.manual_key ?? ""); setOtpauthUri(result.otpauth_uri ?? ""); setPasskeyAuthentication(result.passkey_authentication); setPassword("");
    } catch (reason) { setError(reason instanceof Error ? reason.message : "Sign-in failed"); }
    finally { setBusy(false); }
  }

  async function submitMfa(event: FormEvent) {
    event.preventDefault(); setBusy(true); setError("");
    try {
      const result = await call("/api/v1/auth/password/mfa", { challenge, code });
      authenticationCompleted(result);
    } catch (reason) { setError(reason instanceof Error ? reason.message : "Authenticator verification failed"); }
    finally { setBusy(false); }
  }

  async function submitPasskey() {
    if (!passkeyAuthentication) return;
    setBusy(true); setError("");
    try {
      const credential = await getPasskeyAssertion(passkeyAuthentication);
      const result = await call("/api/v1/auth/password/passkeys/authenticate", {
        challenge_token: passkeyAuthentication.challenge_token,
        credential,
      });
      authenticationCompleted(result);
    } catch (reason) {
      setError(reason instanceof DOMException && reason.name === "NotAllowedError"
        ? "Passkey verification was cancelled or timed out."
        : reason instanceof Error ? reason.message : "Passkey verification failed");
    } finally { setBusy(false); }
  }

  async function enrollPasskey() {
    if (!postAuthentication?.redirect_url) return;
    setBusy(true); setError("");
    try {
      const tenant = postAuthentication.tenant_id;
      if (!tenant) throw new Error("The authenticated tenant binding is missing.");
      const suffix = `?tenant=${encodeURIComponent(tenant)}`;
      const options = await call<PasskeyEnrollmentOptions & { error?: { message?: string } }>(
        `/api/v1/auth/password/passkeys/enrollment/options${suffix}`, {},
      );
      const credential = await createPasskeyCredential(options);
      await call(`/api/v1/auth/password/passkeys/enrollment/verify${suffix}`, { challenge_token: options.challenge_token, credential });
      location.assign(postAuthentication.redirect_url);
    } catch (reason) {
      setError(reason instanceof DOMException && reason.name === "NotAllowedError"
        ? "Passkey enrollment was cancelled or timed out. You can continue and add one later."
        : reason instanceof Error ? reason.message : "Passkey enrollment failed");
    } finally { setBusy(false); }
  }

  if (postAuthentication?.redirect_url) return <div className="mk-password-login mk-passkey-enrollment">
    <div className="mk-password-step"><Fingerprint /><div><strong>Add phishing-resistant MFA</strong><span>Save a passkey to this device, a security key, or your passkey provider. Your authenticator code remains available as a recovery factor.</span></div></div>
    {error && <p className="mk-auth-inline-error" role="alert">{error}</p>}
    <button className="mk-auth-primary mk-auth-primary--button" type="button" disabled={busy} onClick={() => void enrollPasskey()}>{busy ? <><RefreshCw className="spin" /> Adding passkey...</> : <><Fingerprint /> Add a passkey</>}</button>
    <button className="mk-auth-link-button" type="button" disabled={busy} onClick={() => location.assign(postAuthentication.redirect_url!)}>Continue without a passkey</button>
  </div>;

  if (challenge) return <form className="mk-password-login" onSubmit={(event) => void submitMfa(event)}>
    <div className="mk-password-step"><ShieldCheck /><div><strong>{manualKey ? "Secure your account with MFA" : "Enter your authenticator code"}</strong><span>{manualKey ? "Scan this QR code with your authenticator app (or enter the key), then type the current six-digit code." : "Sectigo Edge requires a second factor for password accounts."}</span></div></div>
    {manualKey && totpQrDataUri(otpauthUri) && <img className="mk-totp-qr" src={totpQrDataUri(otpauthUri)} width={200} height={200} alt="QR code for authenticator app enrollment" />}
    {manualKey && <div className="mk-totp-key"><small>MANUAL SETUP KEY</small><code>{manualKey}</code><button type="button" onClick={() => void navigator.clipboard.writeText(manualKey)}>Copy</button></div>}
    {passkeyAuthentication && passkeysSupported() && <>
      <button className="mk-passkey-button" type="button" disabled={busy} onClick={() => void submitPasskey()}><Fingerprint /> Use a passkey</button>
      <div className="mk-auth-divider mk-auth-divider--compact"><span>or use an authenticator code</span></div>
    </>}
    <label><span>Six-digit code</span><input inputMode="numeric" autoComplete="one-time-code" pattern="[0-9]{6}" maxLength={6} value={code} onChange={(event) => setCode(event.target.value.replace(/\D/g, "").slice(0, 6))} autoFocus required /></label>
    {error && <p className="mk-auth-inline-error" role="alert">{error}</p>}
    <button className="mk-auth-primary mk-auth-primary--button" disabled={busy || code.length !== 6}>{busy ? <><RefreshCw className="spin" /> Verifying...</> : <>Verify and open console <ArrowRight /></>}</button>
    <button className="mk-auth-link-button" type="button" onClick={() => { setChallenge(""); setCode(""); setManualKey(""); setPasskeyAuthentication(undefined); setError(""); }}>Use a different account</button>
  </form>;

  return <form className="mk-password-login" onSubmit={(event) => void submitPassword(event)}>
    <label><span>Work email (your username)</span><input type="email" autoComplete="username" placeholder="you@company.com" value={email} onChange={(event) => setEmail(event.target.value)} required maxLength={320} autoFocus /></label>
    <label><span>Password</span><input type="password" autoComplete="current-password" value={password} onChange={(event) => setPassword(event.target.value)} required minLength={12} maxLength={128} /></label>
    {notice && <p className="mk-auth-inline-notice" role="status">{notice}</p>}
    {error && <p className="mk-auth-inline-error" role="alert">{error}</p>}
    <button className="mk-auth-primary mk-auth-primary--button" disabled={busy}>{busy ? <><RefreshCw className="spin" /> Signing in...</> : <><KeyRound /> Sign in with email &amp; password</>}</button>
    <a className="mk-password-recovery-link" href="/forgot-password">Forgot your password?</a>
  </form>;
}

interface RecoveryResult {
  accepted?: boolean;
  reset?: boolean;
  message?: string;
  development_reset_url?: string;
  redirect_url?: string;
  error?: { message?: string };
}

export function PasswordRecoveryPage() {
  const completing = location.pathname.replace(/\/+$/, "") === "/reset-password";
  const initialToken = completing ? new URLSearchParams(location.hash.slice(1)).get("token") ?? "" : "";
  const [token] = useState(initialToken);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [confirmation, setConfirmation] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [result, setResult] = useState<RecoveryResult>();

  useEffect(() => {
    if (completing && token) history.replaceState(null, "", "/reset-password");
  }, [completing, token]);

  async function call(path: string, body: unknown): Promise<RecoveryResult> {
    const response = await fetch(path, { method: "POST", headers: { "content-type": "application/json" }, body: JSON.stringify(body) });
    const payload = await response.json().catch(() => ({})) as RecoveryResult;
    if (!response.ok) throw new Error(payload.error?.message ?? "Password recovery could not be completed");
    return payload;
  }

  async function submitRequest(event: FormEvent) {
    event.preventDefault(); setBusy(true); setError("");
    try { setResult(await call("/api/v1/auth/password/recovery/request", { email })); }
    catch (reason) { setError(reason instanceof Error ? reason.message : "Password recovery could not be started"); }
    finally { setBusy(false); }
  }

  async function submitReset(event: FormEvent) {
    event.preventDefault(); setError("");
    if (!token) { setError("This password reset link is missing or invalid. Request a new link."); return; }
    if (password !== confirmation) { setError("The new passwords do not match."); return; }
    setBusy(true);
    try { setResult(await call("/api/v1/auth/password/recovery/complete", { token, password })); setPassword(""); setConfirmation(""); }
    catch (reason) { setError(reason instanceof Error ? reason.message : "Password recovery could not be completed"); }
    finally { setBusy(false); }
  }

  const finished = result?.accepted || result?.reset;
  return <div className="mk-page mk-auth-page">
    <Nav />
    <main className="mk-auth-shell">
      <section className="mk-auth-copy">
        <div className="mk-kicker"><span /> SECTIGO EDGE</div>
        <h1>{completing ? "Restore access. Preserve trust." : "Recover access without weakening trust."}</h1>
        <p>A password reset never bypasses your passkey or authenticator MFA. Completing it revokes every active session and pending authentication ceremony.</p>
        <div className="mk-auth-points">
          <span><ShieldCheck /> Single-use, 15-minute recovery link</span>
          <span><Fingerprint /> Existing MFA factors remain required</span>
          <span><LockKeyhole /> Sessions and outstanding challenges are invalidated</span>
        </div>
      </section>
      <section className="mk-auth-card mk-auth-card--signup">
        <Logo />
        <small>SECURE ACCOUNT RECOVERY</small>
        <h2>{completing ? "Choose a new password" : "Reset your password"}</h2>
        {finished ? <div className="mk-signup-success mk-recovery-success">
          <span><CircleCheckBig /></span>
          <h2>{result?.reset ? "Password changed" : "Check your work email"}</h2>
          <p>{result?.reset ? "Every active session was revoked. Sign in with your new password and existing MFA factor." : result?.message}</p>
          {result?.development_reset_url && <a className="mk-auth-primary" href={result.development_reset_url}>Open local reset link <ArrowRight /></a>}
          {!result?.development_reset_url && <a className="mk-auth-primary" href="/login">Return to login <ArrowRight /></a>}
        </div> : completing ? <form className="mk-password-login" onSubmit={(event) => void submitReset(event)}>
          {!token && <p className="mk-auth-inline-error" role="alert">This reset link is missing or invalid. Request a new one.</p>}
          <label><span>New password</span><input type="password" autoComplete="new-password" value={password} onChange={(event) => setPassword(event.target.value)} required minLength={12} maxLength={128} /></label>
          <label><span>Confirm new password</span><input type="password" autoComplete="new-password" value={confirmation} onChange={(event) => setConfirmation(event.target.value)} required minLength={12} maxLength={128} /></label>
          {error && <p className="mk-auth-inline-error" role="alert">{error}</p>}
          <button className="mk-auth-primary mk-auth-primary--button" disabled={busy || !token || password.length < 12 || confirmation.length < 12}>{busy ? <><RefreshCw className="spin" /> Changing password...</> : <>Change password <ArrowRight /></>}</button>
          <a className="mk-password-recovery-link" href="/forgot-password">Request a new reset link</a>
        </form> : <form className="mk-password-login" onSubmit={(event) => void submitRequest(event)}>
          <p>Enter the work email used for your Sectigo Edge account. The response is deliberately identical whether or not an account exists.</p>
          <label><span>Work email</span><input type="email" autoComplete="username" placeholder="you@company.com" value={email} onChange={(event) => setEmail(event.target.value)} required maxLength={320} /></label>
          {error && <p className="mk-auth-inline-error" role="alert">{error}</p>}
          <button className="mk-auth-primary mk-auth-primary--button" disabled={busy}>{busy ? <><RefreshCw className="spin" /> Sending secure link...</> : <>Send reset link <ArrowRight /></>}</button>
          <a className="mk-password-recovery-link" href="/login">Return to login</a>
        </form>}
        <footer>Password recovery never grants workspace authority or removes MFA.</footer>
      </section>
    </main>
  </div>;
}

export function AuthPage({ mode }: { mode: "login" | "signup" }) {
  const login = mode === "login";
  const [loginMethod, setLoginMethod] = useState<"password" | "sso">("password");
  const verification =
    !login && location.pathname.replace(/\/+$/, "") === "/signup/verify";
  return (
    <div className="mk-page mk-auth-page">
      <Nav />
      <main className="mk-auth-shell">
        <section className="mk-auth-copy">
          <div className="mk-kicker">
            <span /> SECTIGO EDGE
          </div>
          <h1>
            {login
              ? "Welcome back to the edge."
              : verification
                ? "Prove ownership. Keep authority separate."
                : "Put your PKI at the edge."}
          </h1>
          <p>
            {login
              ? "Use your Sectigo Edge account or your organization’s identity provider. MFA protects both production sign-in paths."
              : "Create a password-protected Sectigo Edge account. Workspace authority follows verified mailbox and DNS ownership plus explicit authorization."}
          </p>
          <div className="mk-auth-points">
            <span>
              <ShieldCheck /> Password + passkey/TOTP or enterprise SSO
            </span>
            <span>
              <Fingerprint /> Fine-grained, resource-aware access
            </span>
            <span>
              <LockKeyhole /> Encrypted sessions and durable lifecycle controls
            </span>
          </div>
        </section>
        <section className="mk-auth-card mk-auth-card--signup">
          <Logo />
          {login ? (
            <>
              <small>SECURE WORKSPACE LOGIN</small>
              <h2>Choose how to sign in</h2>
              <div className="mk-auth-methods" role="tablist" aria-label="Choose a sign-in method">
                <button type="button" role="tab" aria-selected={loginMethod === "password"} className={loginMethod === "password" ? "is-active" : ""} onClick={() => setLoginMethod("password")}>
                  <KeyRound /> Email &amp; password
                </button>
                <button type="button" role="tab" aria-selected={loginMethod === "sso"} className={loginMethod === "sso" ? "is-active" : ""} onClick={() => setLoginMethod("sso")}>
                  <Building2 /> Enterprise SSO
                </button>
              </div>
              <div className="mk-auth-method-panel" role="tabpanel">
                {loginMethod === "password" ? <>
                  <p>Use the work email registered as your username and your password. You’ll verify with a passkey or authenticator code next.</p>
                  <PasswordLogin />
                </> : <>
                  <p>Continue through your organization’s identity provider. Enterprise SSO must enforce MFA and an immutable user identity.</p>
                  <EnterpriseSsoButton />
                </>}
              </div>
              <div className="mk-auth-divider"><span>product preview</span></div>
              <a className="mk-auth-demo" href="/console/demo">
                <Gauge /> Explore the live product demo
              </a>
              <p className="mk-auth-note">
                The demo uses isolated sample data and never grants production
                access.
              </p>
            </>
          ) : verification ? (
            <WorkspaceVerification />
          ) : (
            <>
              <small>NEW SECTIGO EDGE WORKSPACE</small>
              <h2>Create with email &amp; password</h2>
              <p>
                Your work email becomes your username. Choose a password now;
                MFA enrollment follows workspace approval. Enterprise SSO can
                be connected later without removing this account path.
              </p>
              <WorkspaceSignup />
            </>
          )}
          <footer>
            {login ? (
              <>
                Need a workspace? <a href="/signup">Create account</a>
              </>
            ) : (
              <>
                Already provisioned? <a href="/login">Log in</a>
              </>
            )}
          </footer>
        </section>
      </main>
      <Footer />
    </div>
  );
}

function Code({ children }: { children: string }) {
  return (
    <pre>
      <button
        onClick={() => void navigator.clipboard?.writeText(children)}
        aria-label="Copy code"
      >
        <FileCheck2 /> Copy
      </button>
      <code>{children}</code>
    </pre>
  );
}

export function DocsPage() {
  return (
    <div className="mk-page mk-docs">
      <Nav docs />
      <div className="mk-doc-layout">
        <aside>
          <span>DOCUMENTATION</span>
          <a href="#overview">Overview</a>
          <a href="#quickstart">Local quick start</a>
          <a href="#architecture">Architecture</a>
          <a href="#security">Security model</a>
          <a href="#policy-hierarchy">Three-level policy</a>
          <a href="#policy-impact">Evidence-bound policy impact</a>
          <a href="#identity">SSO, MFA, SCIM + OpenFGA</a>
          <a href="#hardware">Hardware identity</a>
          <a href="#evidence">Portable evidence</a>
          <a href="#otlp-audit">OpenTelemetry audit export</a>
          <a href="#rotation">Dual-slot rotation</a>
          <a href="#nginx">NGINX deployment</a>
          <a href="#apache">Apache deployment</a>
          <a href="#java-keystore">Java PKCS#12 deployment</a>
          <a href="#hashicorp-vault">HashiCorp Vault delivery</a>
          <a href="#ca-connectors">CA connectors</a>
          <a href="#revocation">Revocation distribution</a>
          <a href="#integrations">Integration lifecycle</a>
          <a href="#acme">ACME accounts + failover</a>
          <a href="#sdk">SDK discovery</a>
          <a href="#agents">Agent and MCP trust</a>
          <a href="#mcp-management">Management MCP + OAuth</a>
          <a href="#local-ui">Local Mesh Client UI</a>
          <a href="#operations">Production operations</a>
          <a href="/llms.txt">LLM index</a>
        </aside>
        <main>
          <header id="overview">
            <div className="mk-doc-kicker">
              <BookOpen /> SECTIGO EDGE DOCUMENTATION
            </div>
            <h1>
              Build private PKI where
              <br />
              no one operates alone.
            </h1>
            <p>
              Architecture, deployment, and integration guidance for the Sectigo
              Edge control plane and customer Mesh Nodes.
            </p>
            <div>
              <a href="#quickstart">
                Run it locally <ArrowRight />
              </a>
              <a href="/console/demo">Product console</a>
            </div>
          </header>
          <article id="quickstart">
            <span className="mk-doc-label">GET STARTED</span>
            <h2>Local vertical slice</h2>
            <p>
              The development environment runs the Sectigo Edge service, a local
              development CA, the React console, and the Go Mesh Node.
              Production signing is deliberately disabled in this mode.
            </p>
            <Code>{`npm install\nnpm run build\nnpm run dev\n\n# In a second terminal\ngo run ./node/cmd/edgepki-node -config ./node/config.dev.json`}</Code>
            <div className="mk-doc-callout">
              <Zap />
              <p>
                <b>Try managed rotation.</b> POST to{" "}
                <code>/v1/managed/rotate</code> on the node with a SPIFFE
                requester, DNS SANs, and <code>autoPromote: true</code>.
              </p>
            </div>
          </article>
          <article id="architecture">
            <span className="mk-doc-label">SYSTEM DESIGN</span>
            <h2>Two trust domains, one transaction</h2>
            <p>
              A canonical issuance transaction binds the workload, CSR, SANs,
              algorithm, validity, policy version, nonce, node, and expiry. The
              customer node signs it only after local policy allows it. The edge
              verifies that approval and evaluates the same bytes independently
              before invoking the CA provider.
            </p>
            <TrustDiagram />
            <h3>Sectigo Edge responsibilities</h3>
            <div className="mk-doc-cards">
              <div>
                <Globe2 />
                <b>Global trust API</b>
                <p>
                  Mutual TLS ingress, threat controls, and SSO session
                  verification.
                </p>
              </div>
              <div>
                <Layers3 />
                <b>Tenant trust state</b>
                <p>
                  Serialized per-tenant nonces, policy, and issuance decisions.
                </p>
              </div>
              <div>
                <Network />
                <b>Evidence services</b>
                <p>
                  Inventory projections, integrations, and portable evidence
                  exports.
                </p>
              </div>
            </div>
          </article>
          <article id="security">
            <span className="mk-doc-label">THREAT MODEL</span>
            <h2>Security invariants</h2>
            <ul className="mk-doc-checks">
              <li>
                <Check /> Neither Sectigo Edge nor the customer trust domain can
                issue alone.
              </li>
              <li>
                <Check /> Optional replica quorum requires distinct nodes to
                sign the identical transaction.
              </li>
              <li>
                <Check /> Transaction IDs and nonces cannot be rebound or
                replayed.
              </li>
              <li>
                <Check /> Local discovery is algorithm-tagged, SPKI-pinned, and
                signature verified.
              </li>
              <li>
                <Check /> Policy revisions are tenant-bound, signed by a
                separately pinned authority, and chained to reject rollback,
                forks, or gaps.
              </li>
              <li>
                <Check /> mTLS transport and application signatures bind machine
                calls.
              </li>
              <li>
                <Check /> Production private CA keys never enter application
                storage or logs.
              </li>
              <li>
                <Check /> A local policy denial never triggers cloud fallback.
              </li>
            </ul>
            <p>
              Mesh Nodes atomically retain their last verified active policy
              during a control-plane outage, while every invalid update fails
              closed. See the repository threat model and production gates
              before any public launch. The included development CA and policy
              authority exist only for end-to-end testing and must never be
              promoted.
            </p>
          </article>
          <article id="policy-hierarchy">
            <span className="mk-doc-label">MOST RESTRICTIVE WINS</span>
            <h2>Three policy levels, one provable result</h2>
            <p>
              Every request must pass a separately versioned Sectigo platform
              baseline, the customer's signed enterprise policy, and one exact
              workload or application profile. The effective result is the
              intersection: a lower layer can narrow a control but can never
              re-enable what a higher layer forbids.
            </p>
            <div className="mk-doc-cards">
              <div><ShieldCheck /><b>Sectigo platform</b><p>Algorithm, transport, lifetime, offline-renewal, deployment, evidence, and distinct-node quorum floor.</p></div>
              <div><Building2 /><b>Enterprise</b><p>Tenant-approved defaults, PQC transition, eligible nodes, and certificate lifecycle bounds.</p></div>
              <div><Boxes /><b>Workload profile</b><p>Exact SPIFFE paths, DNS namespaces, algorithms, adapters, evidence, rotation, health, and rollback.</p></div>
            </div>
            <div className="mk-doc-callout">
              <Fingerprint />
              <p><b>No silent baseline change.</b> The platform ID, version, and canonical digest are committed into impact approval, pinned by the offline-signed protected-signer registry, retained in every transaction, and re-applied by the portable verifier.</p>
            </div>
            <Code>{`GET /api/v1/policy/platform

{
  "policy": { "id": "sectigo-private-pki-baseline-2026", "version": 2 },
  "sha256": "<canonical-base64url-digest>"
}`}</Code>
          </article>
          <article id="policy-impact">
            <span className="mk-doc-label">SAFE CRYPTOGRAPHIC CHANGE</span>
            <h2>Approve the exact policy against the exact estate</h2>
            <p>
              Before a cryptographic policy enters two-person approval, the
              tenant control plane unions managed certificate sets, in-flight
              issuance, and every endpoint's latest capability evidence. The
              proposal commits that complete canonical assessment, not a
              dashboard percentage or a sampled inventory.
            </p>
            <div className="mk-doc-cards">
              <div><Network /><b>Complete endpoint union</b><p>Missing endpoints remain visible; expired evidence cannot make an application disappear.</p></div>
              <div><Fingerprint /><b>Bounded full-estate commitment</b><p>Every endpoint is digest-bound while human-review details stay safely bounded for large estates.</p></div>
              <div><ShieldCheck /><b>Approval-time recomputation</b><p>Any platform baseline, evidence, expiry, profile mapping, endpoint, count, or enterprise policy change invalidates approval.</p></div>
            </div>
            <p>
              Cryptographic changes fail closed on missing, stale, ambiguous,
              imported-only, or incompatible capability evidence. A different
              immutable SSO subject with fresh MFA may activate only a current,
              non-blocking <code>impact_sha256</code>. This turns PQC and
              algorithm migration into a measured safety decision rather than
              an optimistic configuration toggle.
            </p>
            <Code>{`POST /api/v1/policy/impact
{ "policy": { "version": 13, "transition": { "mode": "hybrid" } } }

PUT /api/v1/policy
{ "policy": { "version": 13 }, "impact_sha256": "..." }`}</Code>
          </article>
          <article id="identity">
            <span className="mk-doc-label">ENTERPRISE ACCESS</span>
            <h2>Authentication and authorization remain separate</h2>
            <p>
              Sectigo Edge federates the enterprise SAML or OIDC provider and
              verifies its signed assertion, exact application audience,
              immutable subject, expiry, and MFA assurance. Verified work-email
              accounts alternatively use a salted/peppered password plus TOTP
              or an RP-bound WebAuthn passkey. Tenant-bound SCIM
              then confirms the subject is still active and supplies current
              groups. Finally, fine-grained policy decides the exact action
              against the exact tenant, CA, node, policy, certificate set, or
              audit stream.
            </p>
            <div className="mk-doc-cards">
              <div>
                <LockKeyhole />
                <b>SSO or account + MFA</b>
                <p>
                  Passkeys provide phishing-resistant MFA. Deployments can
                  require that assurance plus a session issued within ten
                  minutes for every privileged mutation.
                </p>
              </div>
              <div>
                <Users />
                <b>SCIM lifecycle</b>
                <p>
                  Each tenant gets its own one-time credential and URL. Only the
                  token digest is retained; rotation invalidates the old token
                  immediately.
                </p>
              </div>
              <div>
                <ShieldEllipsis />
                <b>Resource authorization</b>
                <p>
                  Six simple product roles compile to fine-grained relationship
                  rules and current group membership.
                </p>
              </div>
            </div>
            <div className="mk-doc-callout">
              <ShieldCheck />
              <p>
                <b>No duplicate directory setup.</b> With SCIM required, role
                assignment resolves a user email through the provisioned SCIM
                directory to the immutable subject stored in the authorization
                graph.
              </p>
            </div>
          </article>
          <article id="hardware">
            <span className="mk-doc-label">MESH NODE IDENTITY</span>
            <h2>Keep approval keys inside hardware</h2>
            <p>
              Production nodes select one explicit provider: Microsoft Platform
              Crypto Provider on Windows, native TPM2 or PKCS#11 on Linux, or an
              enterprise HSM module. Sectigo Edge signs through{" "}
              <code>crypto.Signer</code>; the private key is never serialized
              into node state.
            </p>
            <div className="mk-doc-cards">
              <div>
                <TerminalSquare />
                <b>Windows CNG / TPM</b>
                <p>
                  Open a pre-provisioned ECDSA P-256 key by CNG name.
                  Platform-provider keys remain sealed by the TPM.
                </p>
              </div>
              <div>
                <Server />
                <b>Linux TPM2</b>
                <p>
                  Create a non-duplicable P-256 key, persist only TPM-wrapped
                  blobs, and enroll its key-certification evidence.
                </p>
              </div>
              <div>
                <KeyRound />
                <b>PKCS#11 HSM</b>
                <p>
                  Select a vendor token and existing key by ID or label. The PIN
                  comes only from the service secret environment.
                </p>
              </div>
            </div>
            <p>
              Enrollment proves fresh possession of the declared key. The
              control plane records the SPKI fingerprint, protection class, and
              attestation digest; discovery, signer registries, portable
              evidence, and SDK pins carry the same identity.
            </p>
          </article>
          <article id="evidence">
            <span className="mk-doc-label">CUSTOMER-VERIFIABLE PROOF</span>
            <h2>Take the evidence with you</h2>
            <p>
              An auditor can export a content-addressed proof for any issuance
              or revocation. It contains every distinct node approval, both
              policy decisions, the protected signer receipt, the exact
              independent registry generation, and a contiguous audit path to a
              fresh signed checkpoint.
            </p>
            <div className="mk-doc-callout">
              <Fingerprint />
              <p>
                <b>No embedded-key trust.</b> The offline verifier requires
                customer-pinned node, signer, registry, and audit key
                generations. A compromised control plane cannot make an
                attacker-selected key trustworthy by placing it in the bundle.
              </p>
            </div>
            <Code>{`npx sharppki-verify \\\n  --evidence sharppki-evidence-tx_123.json \\\n  --trust pinned-trust.json`}</Code>
          </article>
          <article id="otlp-audit">
            <span className="mk-doc-label">SIEM AND EVIDENCE DELIVERY</span>
            <h2>Stream the signed node journal without trusting the stream</h2>
            <p>
              Each Mesh Node can send its complete signed, hash-chained audit
              journal to an OpenTelemetry Collector over OTLP/HTTP JSON. The
              collector address, CA roots, and client identity remain local.
              Every log body carries the original node signature; searchable
              attributes are a projection, never a replacement for that proof.
            </p>
            <div className="mk-doc-cards">
              <div><LockKeyhole /><b>TLS 1.3 mTLS only</b><p>Exact <code>/v1/logs</code> endpoint, pinned CA, client-auth EKU, no redirects, proxy, query credentials, or ambient authority.</p></div>
              <div><Fingerprint /><b>Signed restart cursor</b><p>Tenant, node, endpoint digest, journal boundary, sequence, and event hash are signed and reverified before resume.</p></div>
              <div><RefreshCw /><b>At-least-once replay</b><p>Any timeout, rejection, malformed reply, or partial success retains the cursor. A stable batch digest supports deduplication.</p></div>
            </div>
            <Code>{`{
  "otlp_audit_enabled": true,
  "otlp_audit_exporter_id": "primary-siem",
  "otlp_audit_endpoint": "https://otel.customer.example:4318/v1/logs",
  "otlp_audit_trust_bundle_file": "/etc/sectigo-edge/otel/ca.pem",
  "otlp_audit_client_certificate_file": "/etc/sectigo-edge/otel/client.pem",
  "otlp_audit_client_private_key_file": "/etc/sectigo-edge/otel/client-key.pem",
  "otlp_audit_batch_size": 32,
  "otlp_audit_request_timeout_seconds": 10,
  "otlp_audit_poll_interval_seconds": 5
}`}</Code>
            <p>
              Health, acknowledged cursor, journal head, backlog, last complete
              acceptance, and bounded failure code appear in node health,
              Prometheus, integration status, and the local operations UI. The
              release gate exercises the path against a digest-pinned real
              OpenTelemetry Collector with mandatory client certificates.
            </p>
          </article>
          <article id="rotation">
            <span className="mk-doc-label">CERTIFICATE LIFECYCLE</span>
            <h2>Dual-slot, zero-downtime rotation</h2>
            <p>
              Set <code>deploymentMode</code> to <code>dual_slot</code>. The
              first certificate becomes active. Renewals become standby, are
              written atomically with restrictive key permissions, and cannot
              replace active until the configured health probe succeeds.
            </p>
            <RotationDiagram />
          </article>
          <article id="nginx">
            <span className="mk-doc-label">LINUX SERVER DEPLOYMENT</span>
            <h2>NGINX rotation that proves what is serving</h2>
            <p>
              Enable the Linux NGINX adapter to move from issued certificate to
              verified live deployment without a shell hook. The Mesh Node pins
              the exact NGINX executable, stages immutable generations, validates
              the expanded managed include, performs a graceful reload, and
              compares the live TLS leaf to the exact staged certificate.
            </p>
            <div className="mk-doc-cards">
              <div>
                <Fingerprint />
                <b>Pinned execution</b>
                <p>Fixed arguments and an executable SHA-256 checked every time.</p>
              </div>
              <div>
                <RefreshCw />
                <b>Atomic promotion</b>
                <p>Immutable generations and a durable current pointer.</p>
              </div>
              <div>
                <ShieldCheck />
                <b>Exact-cert health</b>
                <p>A generic 200 response cannot validate the wrong certificate.</p>
              </div>
            </div>
            <Code>{`# Include once in the TLS server block
include /var/lib/sectigo-edge/nginx/sectigo-edge-tls.conf;

# Pin the installed executable
sha256sum /usr/sbin/nginx`}</Code>
            <p>
              Configure the adapter from <b>Integrations → NGINX</b>. The node
              must acknowledge the exact cloud desired-state revision before a
              rotation can activate it. The complete host layout, node JSON,
              permissions, health constraints, and failure behavior are in the
              repository's NGINX integration guide.
            </p>
          </article>
          <article id="apache">
            <span className="mk-doc-label">LINUX SERVER DEPLOYMENT</span>
            <h2>Apache graceful rotation with durable rollback</h2>
            <p>
              The Apache HTTP Server adapter pins the exact executable, writes
              immutable certificate generations, proves the managed snippet is
              present in Apache's include layout, performs a fixed-command
              graceful restart, and verifies the live leaf before committing.
            </p>
            <div className="mk-doc-cards">
              <div>
                <Fingerprint />
                <b>Pinned execution</b>
                <p>No command template or privileged shell hook is accepted.</p>
              </div>
              <div>
                <RefreshCw />
                <b>Connection-safe reload</b>
                <p>Apache re-reads configuration while open connections drain.</p>
              </div>
              <div>
                <ShieldCheck />
                <b>Crash-safe authority</b>
                <p>The key, CSR, profile, adapter, and revision remain durably bound.</p>
              </div>
            </div>
            <Code>{`# Include once inside the target TLS VirtualHost
Include "/var/lib/sectigo-edge/apache/sectigo-edge-tls.conf"

# Pin the installed executable
sha256sum /usr/sbin/apache2`}</Code>
            <p>
              Configure <b>Integrations → Apache HTTP Server</b>, then allow the
              <code> apache </code> deployment mode only in the workload profiles
              authorized to activate it. See the Apache integration guide for
              the complete node configuration and recovery model.
            </p>
          </article>
          <article id="java-keystore">
            <span className="mk-doc-label">WINDOWS + LINUX JVM DEPLOYMENT</span>
            <h2>Java PKCS#12 rotation without shared cloud secrets</h2>
            <p>
              The Java adapter turns a signed issuance result into an immutable,
              password-protected PKCS#12 generation. The Mesh Node verifies the
              certificate and key in process, pins JDK <code>keytool</code> and
              the application reload executable by exact SHA-256, atomically
              replaces the active keystore, and requires the live endpoint to
              serve the staged leaf before promotion commits.
            </p>
            <div className="mk-doc-cards">
              <div>
                <Fingerprint />
                <b>Secret-local PKCS#12</b>
                <p>The password stays in a private host file and never enters cloud desired state.</p>
              </div>
              <div>
                <RefreshCw />
                <b>Atomic reload + rollback</b>
                <p>Windows and Linux replacement preserves the exact prior generation on failure.</p>
              </div>
              <div>
                <ShieldCheck />
                <b>Policy-bound activation</b>
                <p>Node, profile, adapter, policy, revision, slot, and certificate digest must all match.</p>
              </div>
            </div>
            <Code>{`# Resolve symlinks and pin both execution boundaries
KEYTOOL="$(readlink -f "$(command -v keytool)")"
sha256sum "$KEYTOOL" /opt/example/bin/reload-tls

# Password is a private local file; keytool uses -storepass:file
chmod 0600 /etc/sectigo-edge/java-keystore.pass`}</Code>
            <p>
              Configure <b>Integrations → Java PKCS#12</b>, then permit
              <code> java_keystore </code> only in the intended workload
              profiles. Dual-slot rotation, health grace, promotion mode, and
              rollback window are policy controlled. The full integration guide
              covers Linux and Windows host preparation, node JSON, lifecycle,
              crash recovery, and real-JDK qualification.
            </p>
          </article>
          <article id="hashicorp-vault">
            <span className="mk-doc-label">HASHICORP VAULT KV V2 DELIVERY</span>
            <h2>Vault delivery with check-and-set rotation</h2>
            <p>
              The Vault adapter writes each issued certificate and key as a
              write-once KV v2 generation, then moves a check-and-set protected
              pointer that consumers and Vault Agent templates follow. The Mesh
              Node keeps the Vault token in a private local file, never contacts
              Vault at startup, and requires the live endpoint to serve the
              staged leaf before promotion commits.
            </p>
            <div className="mk-doc-cards">
              <div>
                <Fingerprint />
                <b>Token stays local</b>
                <p>The Vault token is read from a private host file and never enters cloud desired state.</p>
              </div>
              <div>
                <RefreshCw />
                <b>CAS pointer + rollback</b>
                <p>Generations are never overwritten; a failed health check restores the previous pointer.</p>
              </div>
              <div>
                <ShieldCheck />
                <b>Policy-bound activation</b>
                <p>Node, profile, adapter, policy, revision, slot, and certificate digest must all match.</p>
              </div>
            </div>
            <Code>{`# Narrow token: write generations + pointer, read metadata
vault policy write sectigo-edge-prod sectigo-edge-prod.hcl
vault token create -policy=sectigo-edge-prod -period=24h -field=token \\
  > /etc/sectigo-edge/vault.token && chmod 0600 /etc/sectigo-edge/vault.token

# Consumers read <base>/pointer, then the active generation
vault kv get -mount=secret sectigo-edge/prod-service/pointer`}</Code>
            <p>
              Configure <b>Integrations → HashiCorp Vault KV v2</b>, then permit
              <code> hashicorp_vault </code> only in the intended workload
              profiles. The full integration guide covers the Vault policy, node
              JSON, data layout, optional reload command, lifecycle, and crash
              recovery.
            </p>
          </article>
          <article id="ca-connectors">
            <span className="mk-doc-label">SIGNING PROVIDERS</span>
            <h2>Sectigo SCM and BYO CA</h2>
            <p>
              The console creates a one-time connector session. SCM uses an
              authorization redirect to select the managed CA. BYO CA uses an
              expiring upload ceremony terminating at the protected remote
              signer—not at the application service. Connector records contain
              the certificate chain, opaque signer key reference, and ceremony
              receipt.
            </p>
            <p>
              The signing boundary is independently protected, authenticates
              Sectigo Edge with mTLS, and re-verifies the complete node quorum,
              policy version, CSR, tenant, provider, key reference, and issuer
              pin. Every result carries an Ed25519 receipt that Sectigo Edge
              validates before accepting the certificate.
            </p>
            <Code>{`POST /api/v1/cas/import-session\n{\n  "provider": "sectigo_scm",\n  "name": "Production issuing CA"\n}`}</Code>
          </article>
          <article id="revocation">
            <span className="mk-doc-label">CERTIFICATE STATUS</span>
            <h2>Complete, monotonic CRLs that fail closed</h2>
            <p>
              A revocation takes effect even when distribution is temporarily unavailable. The protected signer—or the exact assigned Windows node for ADCS—then produces or retrieves a CA-signed CRL containing every known revocation and independently verifies its issuer pin, signature, freshness, entries, reasons, and strictly increasing CRL number. Sectigo Edge repeats those checks before immutable publication.
            </p>
            <div className="mk-doc-cards">
              <div><ShieldCheck /><b>Complete set</b><p>An omitted or changed serial, reason, or revocation time is rejected.</p></div>
              <div><RefreshCw /><b>Rollback resistant</b><p>Every generation binds the prior digest and CRL number.</p></div>
              <div><Network /><b>Fail-closed delivery</b><p>Stale or missing artifacts return unavailable instead of unsafe cached bytes.</p></div>
            </div>
            <p>
              Every authority maintains separate SHA-1 and SHA-256 OCSP CertID responses. SCM and BYO authorities use the protected signer; ADCS uses the exact assigned Windows node and its pinned Microsoft Online Responder. The node and edge independently verify responder authority, certificate status, signature, and freshness before fail-closed GET/POST delivery. OCSP and CRL health remain separate.
            </p>
            <Code>{`GET  /pki/{tenant}/crl/{connector}.crl\nPOST /pki/{tenant}/ocsp/{connector}\nGET  /api/v1/revocation/ocsp?limit=100\nPOST /api/v1/revocation/ocsp/{transaction}/refresh\n{ "confirmation": "REFRESH OCSP" }`}</Code>
            <p>
              The public DER endpoint needs no browser session. Operator status and refresh remain protected by enterprise identity, resource authorization, fresh MFA, and immutable audit evidence.
            </p>
          </article>
          <article id="integrations">
            <span className="mk-doc-label">OUTBOUND-ONLY ADAPTERS</span>
            <h2>One lifecycle from demo to production</h2>
            <p>
              Administrators save tenant-scoped desired state without uploading
              connector secrets. Assigned Mesh Nodes poll over their mutually
              authenticated outbound channel, resolve adapter references from
              local encrypted storage, and acknowledge the exact revision as
              healthy or degraded.
            </p>
            <div className="mk-doc-cards">
              <div>
                <LockKeyhole />
                <b>Secrets stay local</b>
                <p>Cloud state contains non-secret references only.</p>
              </div>
              <div>
                <Network />
                <b>Revision-bound health</b>
                <p>
                  Every assigned node must acknowledge the current revision.
                </p>
              </div>
              <div>
                <Check />
                <b>Kubernetes custom signer</b>
                <p>
                  Independent approval, exact-signer RBAC, projected tokens,
                  and durable replay.
                </p>
              </div>
              <div>
                <Laptop />
                <b>Durable Microsoft ADCS</b>
                <p>
                  Native Windows enrollment, Request-ID recovery, CA-signed
                  CRLs, and cloud-verified completion.
                </p>
              </div>
            </div>
            <Code>{`GET  /api/v1/integrations
PUT  /api/v1/integrations/{kind}
POST /api/v1/integrations/{kind}/test

GET  /api/v1/nodes/{node}/integrations
POST /api/v1/nodes/{node}/integrations/{kind}/status`}</Code>
            <div className="mk-doc-callout">
              <ShieldCheck />
              <p>
                <b>Kubernetes approval stays independent.</b> The Mesh Node
                never approves a CSR. It signs only an already-approved request
                for one custom signer after exact label, namespace, service
                account, usage, lifetime, SAN, and tenant-policy validation.
              </p>
            </div>
            <p>
              The Mesh Node also implements RFC 7030 EST for managed devices
              and applications: SPIFFE mTLS authentication, strict PKCS#10,
              CMS certs-only responses, CA-chain pinning, and
              identity-preserving re-enrollment all reuse the same policy,
              quorum, and protected signer transaction. Disablement or node
              unassignment is delivered as an explicit authority-withdrawal
              tombstone.
            </p>
            <p>
              Existing Microsoft enterprise CAs use a first-class local-signing
              handoff after both trust domains approve. The Windows node pins
              one template, persists the native ADCS Request ID, recovers
              pending requests without resubmission, and keeps Kerberos or gMSA
              credentials local. The cloud verifies the returned CSR key,
              exact SANs, authorized validity, adapter revision, and uploaded
              public ADCS chain before it records issuance.
              Revocation uses the same durable handoff: the assigned node calls
              native ADCS publish/retrieve operations, validates the CA-signed
              CRL locally, and uploads it through the node-bound machine
              channel. The edge rejects missing entries, wrong reasons,
              signature or issuer substitution, stale validity, and CRL-number
              rollback before immutable distribution.
            </p>
          </article>
          <article id="acme">
            <span className="mk-doc-label">RFC 8555 ACME</span>
            <h2>External accounts belong to one workload and one node</h2>
            <p>
              Production account creation requires a short-lived 256-bit
              External Account Binding credential. Its nested signature binds
              the exact account key and new-account URL; the node additionally
              binds it to one immutable enrolled node ID and one exact SPIFFE
              workload identity. A valid credential can create one durably
              persisted account and cannot be replayed on another replica.
            </p>
            <div className="mk-doc-cards">
              <div><Fingerprint /><b>Exact workload</b><p>The live X.509-SVID must match on every account operation.</p></div>
              <div><Server /><b>Stable replica</b><p>Account and order traffic returns to one StatefulSet member.</p></div>
              <div><LockKeyhole /><b>One-time authority</b><p>The HMAC secret expires within 23 hours and is displayed once.</p></div>
              <div><RefreshCw /><b>Explicit failover</b><p>Clients keep independent accounts on named primary and secondary nodes.</p></div>
            </div>
            <Code>{`sectigo-edge \\
  -acme-eab-file /secure/eab-credentials.json \\
  -acme-eab-node-id node_01HZA... \\
  -acme-eab-workload spiffe://customer.example/ns/prod/sa/web \\
  -acme-eab-validity 15m acme-eab-create`}</Code>
            <div className="mk-doc-callout">
              <ShieldCheck />
              <p><b>No random load balancing.</b> Use the pod-specific directory advertised by each node and configure a separate secondary account for transport failover.</p>
            </div>
          </article>
          <article id="sdk">
            <span className="mk-doc-label">APPLICATION INTEGRATION</span>
            <h2>Local discovery with safe cloud fallback</h2>
            <p>
              Applications pin the enrolled node SPKI, fingerprint, hardware
              protection class, and optional attestation digest. The SDK tries
              explicit configuration, the environment, loopback, and site
              discovery. Only transport-level connectivity loss can fail back;
              reachable HTTP errors, authentication, policy, cursor, and
              signature failures remain final.
            </p>
            <Code>{`const sectigoEdge = new SectigoEdgeClient({\n  tenantId: "customer-id",\n  cloudUrl: "https://api.sharppki.com",\n  discoverySigningAlgorithm: "ECDSA-P256-SHA256",\n  discoveryPublicKeySPKIBase64: process.env.SECTIGO_EDGE_NODE_SPKI,\n  discoveryPublicKeySha256: process.env.SECTIGO_EDGE_NODE_FINGERPRINT,\n  discoveryKeyProtection: "tpm2",\n  discoveryAttestationSHA256: process.env.SECTIGO_EDGE_NODE_ATTESTATION,\n  fallback: "connectivity_only"\n});\n\nconst issued = await sectigoEdge.issue({\n  requester: "spiffe://customer/payments",\n  profileId: "tls-server",\n  sans: ["payments.customer.example"],\n  csrDerBase64\n});`}</Code>
            <p>
              Signed trust-event subscriptions use independent local and cloud
              cursors, at-least-once delivery, and pinned source signatures.
              Events can trigger reconciliation but cannot authorize a PKI
              action.
            </p>
            <Code>{`sectigo-edge \\
  -url https://127.0.0.1:9443 \\
  -ca /etc/sectigo-edge/ca.pem \\
  -cert /run/spire/svid.pem -key /run/spire/svid-key.pem \\
  -tenant customer-id \\
  -node-spki /etc/sectigo-edge/node-public.pem \\
  -node-spki-sha256 "$SECTIGO_EDGE_NODE_SPKI_SHA256" \\
  -event-cursor-file /var/lib/sectigo-edge/events.json \\
  -event-topics certificate.promoted,policy.version.available \\
  -event-wait 20s -watch events`}</Code>
          </article>
          <article id="agents">
            <span className="mk-doc-label">
              AGENTIC AND INTERNAL SERVICE TRUST
            </span>
            <h2>Sign actions, not just connections</h2>
            <p>
              Use X.509-SVID mTLS for live connections and the Agent Trust SDK
              for messages that can be queued, delegated, forwarded, or audited.
              Each canonical envelope binds the issuer, exact receiver, action,
              channel resource, body digest, nonce, and a maximum five-minute
              lifetime. Optional delegation chains can only narrow authority.
            </p>
            <AgentTrustDiagram />
            <Code>{`const discovery = await sectigoEdge.discoverNode();\nif (!discovery?.agent_spiffe_id) throw new Error("No trusted local node");\n\nconst agent = new SectigoEdgeAgentClient(signer, trust, replay);\nconst registration = new SectigoEdgeRegistrationClient(agent);\nconst challenge = await postToLocalNode(await registration.announce({\n  nodeAudience: discovery.agent_spiffe_id,\n  runtime_id: "risk-reviewer-42",\n  display_name: "Risk reviewer",\n  requested_spiffe_id: "spiffe://customer/agent/prod/risk-reviewer",\n  protocols: ["mcp", "a2a"],\n  capabilities: ["risk.review"],\n  endpoints: ["https://127.0.0.1:9444"],\n  measurements: runtimeMeasurements,\n  claim_key_spki_base64: claimSPKI,\n  claim_key_spki_sha256: claimFingerprint,\n  lease_seconds: 120\n}));\n\nawait postToLocalNode(await registration.proveClaim(challenge, discovery.agent_spiffe_id));`}</Code>
            <div className="mk-doc-callout">
              <ShieldCheck />
              <p>
                <b>Registration is not authorization.</b> A successful proof
                creates a <code>pending_admin</code> record only. Certificate
                issuance and discovery visibility remain unavailable until an
                MFA-protected administrator claim applies policy.
              </p>
            </div>
          </article>
          <article id="mcp-management">
            <span className="mk-doc-label">OAUTH-SECURED AUTOMATION</span>
            <h2>
              Manage Sectigo Edge through MCP without creating a second control
              plane
            </h2>
            <p>
              The Streamable HTTP management endpoint is an OAuth resource
              server. MCP clients discover the enterprise authorization server
              through protected-resource metadata, use Authorization Code with
              PKCE, and request a token for the exact Sectigo Edge MCP resource.
              The service verifies the token signature, issuer, audience,
              tenant, lifetime, scopes, authentication methods, and a
              sender-constraining DPoP proof before any tool is visible.
            </p>
            <div className="mk-doc-cards">
              <div>
                <LockKeyhole />
                <b>Token and sender binding</b>
                <p>
                  Tokens issued for another API are rejected. ES256 DPoP makes
                  every token usable only with its non-exportable client key.
                </p>
              </div>
              <div>
                <Users />
                <b>OpenFGA on every call</b>
                <p>
                  OAuth scope filters tools; resource-aware authorization still
                  decides each invocation.
                </p>
              </div>
              <div>
                <Fingerprint />
                <b>Hash-chained tool evidence</b>
                <p>
                  User subject, OAuth client, tool, and request ID are committed
                  without logging the token or sensitive arguments.
                </p>
              </div>
            </div>
            <Code>{`GET /.well-known/oauth-protected-resource/mcp\nPOST /mcp\nAuthorization: DPoP <sender-constrained-token>\nDPoP: <single-use ES256 request proof>\n\nScopes:\n  edge:read   edge:audit   edge:rotate\n  edge:admin  edge:users   edge:ca`}</Code>
            <div className="mk-doc-callout">
              <ShieldCheck />
              <p>
                <b>High-impact tools require more than OAuth.</b> Rotation,
                issuance safety controls, access changes, and CA ceremonies
                require OpenFGA permission, MFA within ten minutes, closed
                argument schemas, and exact confirmation strings.
              </p>
            </div>
          </article>
          <article id="local-ui">
            <span className="mk-doc-label">LOCAL OPERATIONS</span>
            <h2>See the real node state without adding a control plane</h2>
            <p>
              Each Mesh Node embeds an optional dependency-free browser view of
              its enrolled identity, signed-policy digest and expiry, audit-chain
              head, real adapter health, issuance counters, and healthy dual-slot
              promotions. It is disabled by default and has no mutation endpoint.
            </p>
            <div className="mk-doc-cards">
              <div><ShieldCheck /><b>Separate operator scope</b><p>Every document, asset, and snapshot requires an allow-listed SPIFFE mTLS operator identity.</p></div>
              <div><LockKeyhole /><b>No browser authority</b><p>No cookies, private keys, third-party assets, policy writes, issuance, or certificate promotion.</p></div>
              <div><Server /><b>Authoritative local state</b><p>An expired or missing signed policy displays Blocked; stale cached state is never treated as current.</p></div>
            </div>
            <Code>{`{
  "local_ui_enabled": true,
  "local_ui_allowed_spiffe_prefixes": [
    "spiffe://customer.example/operators/pki/"
  ]
}

https://127.0.0.1:9443/ui/`}</Code>
            <div className="mk-doc-callout">
              <ShieldCheck />
              <p><b>Preserve end-to-end mTLS.</b> Keep the listener on loopback or a private management network. Do not convert a reverse-proxy identity header into operator authority.</p>
            </div>
          </article>
          <article id="operations">
            <span className="mk-doc-label">PRODUCTION</span>
            <h2>Deployment checklist</h2>
            <div className="mk-doc-cards">
              <div>
                <Users />
                <b>Identity</b>
                <p>
                  Enterprise SAML/OIDC, MFA, SCIM groups, and short privileged
                  sessions.
                </p>
              </div>
              <div>
                <ShieldCheck />
                <b>Authorization</b>
                <p>
                  Redundant fine-grained authorization and multi-zone storage
                  over private connectivity.
                </p>
              </div>
              <div>
                <KeyRound />
                <b>Signing</b>
                <p>
                  Three protected signer replicas, mTLS, signed registry,
                  evidence receipts.
                </p>
              </div>
            </div>
            <p>
              Provision separate staging and production accounts, mutual TLS,
              durable tenant state, asynchronous jobs, evidence storage,
              logs/traces, pinned authorization models, and the CA signer before
              deployment. Custom-domain and DNS cutover remain separate reviewed
              changes.
            </p>
          </article>
        </main>
      </div>
      <Footer />
    </div>
  );
}

function Footer() {
  return (
    <footer className="mk-footer">
      <Logo />
      <div>
        <a href="/docs">Documentation</a>
        <a href="/login">Log in</a>
        <a href="/signup">Create account</a>
        <a href="/console/demo">Demo</a>
        <a href="https://www.sectigo.com/">Sectigo.com</a>
      </div>
      <p>
        © {new Date().getFullYear()} Sectigo Limited. Sectigo Edge is a Sectigo
        product.
      </p>
    </footer>
  );
}
